import { useEffect, useState } from "react";
import { Gift, LockKeyhole } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { formatPrice } from "@/lib/format";
import { useI18n } from "@/lib/i18n";
import { featureEnabled, useStorefront } from "@/lib/storefront";

export function LoyaltyReward() {
  const { t, lang } = useI18n();
  const storefront = useStorefront();
  const { isAuthenticated, openLogin } = useAuth();
  const {
    cart,
    quote,
    quotePending,
    setLoyaltyPoints,
    setPromotionCode,
    clearPromotionAttribution,
  } = useCart();
  const [pointDraft, setPointDraft] = useState("");
  const loyalty = quote?.loyalty;
  const requested = cart.redeemLoyaltyPoints !== undefined;
  const hasPromotion = Boolean(cart.promotionCode || cart.promotionAttribution);
  const applied = loyalty?.applied;
  const appliedPointCost = applied?.pointCost;
  const program = loyalty?.program;
  const minimum = loyalty?.minimumRedeemablePoints ?? 0;
  const maximum = loyalty?.maximumRedeemablePoints ?? 0;
  const selectedPoints = Number(pointDraft);
  const validSelection =
    pointDraft.trim() !== "" &&
    Number.isSafeInteger(selectedPoints) &&
    selectedPoints >= minimum &&
    selectedPoints <= maximum;

  useEffect(() => {
    if (appliedPointCost !== undefined) setPointDraft(String(appliedPointCost));
  }, [appliedPointCost]);

  if (
    storefront.mode === "preview" ||
    !featureEnabled(storefront, "loyalty") ||
    (!program && !requested)
  ) {
    return null;
  }

  return (
    <section
      className="rounded-2xl border border-primary/20 bg-primary/5 p-4"
      aria-labelledby="cart-loyalty-title"
    >
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
          <Gift className="size-5" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 id="cart-loyalty-title" className="font-extrabold">{t("loyalty.cartTitle")}</h3>
          {program?.status === "paused" ? (
            <p className="mt-1 text-sm text-muted-foreground">{t("loyalty.paused")}</p>
          ) : !isAuthenticated ? (
            <div className="mt-2 space-y-3">
              <p className="text-sm text-muted-foreground">{t("loyalty.signInToUse")}</p>
              <Button type="button" size="sm" variant="outline" onClick={() => openLogin()}>
                <LockKeyhole className="size-4" aria-hidden="true" />
                {t("nav.signin")}
              </Button>
            </div>
          ) : hasPromotion && requested ? (
            <div className="mt-2 space-y-3">
              <p role="alert" className="text-sm font-medium text-destructive">
                {t("loyalty.promotionConflict")}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" onClick={() => {
                  setPromotionCode("");
                  clearPromotionAttribution();
                }}>
                  {t("loyalty.keepReward")}
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => setLoyaltyPoints(null)}>
                  {t("loyalty.keepOffer")}
                </Button>
              </div>
            </div>
          ) : !program && requested ? (
            <div className="mt-2 space-y-2">
              <p className="text-sm text-muted-foreground">{t("loyalty.selectionUnavailable")}</p>
              <Button type="button" size="sm" variant="outline" onClick={() => setLoyaltyPoints(null)}>
                {t("loyalty.removeReward")}
              </Button>
            </div>
          ) : program ? (
            <div className="mt-2 space-y-3">
              <p className="text-sm text-muted-foreground">
                {t("loyalty.rewardValue", {
                  points: program.rewardPoints,
                  amount: formatPrice(program.rewardDiscountMinor, lang, program.currency),
                })}
              </p>
              {applied ? (
                <p className="text-sm font-bold text-primary" aria-live="polite">
                  {t("loyalty.rewardApplied", {
                    points: applied.pointCost,
                    amount: formatPrice(applied.discountMinor, lang, program.currency),
                  })}
                </p>
              ) : null}
              {loyalty?.rewardEligible ? (
                <div className="space-y-2">
                  <Label htmlFor="loyalty-points">{t("loyalty.choosePoints")}</Label>
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      id="loyalty-points"
                      type="number"
                      inputMode="numeric"
                      min={minimum}
                      max={maximum}
                      step={1}
                      value={pointDraft}
                      onChange={(event) => setPointDraft(event.target.value)}
                      aria-describedby="loyalty-points-help"
                      aria-invalid={pointDraft !== "" && !validSelection}
                      className="w-32"
                    />
                    <Button
                      type="button"
                      size="sm"
                      disabled={quotePending || !validSelection || applied?.pointCost === selectedPoints}
                      onClick={() => setLoyaltyPoints(selectedPoints)}
                    >
                      {applied ? t("loyalty.updatePoints") : t("loyalty.applyReward")}
                    </Button>
                    {applied ? (
                      <Button type="button" size="sm" variant="ghost" disabled={quotePending} onClick={() => setLoyaltyPoints(null)}>
                        {t("loyalty.removeReward")}
                      </Button>
                    ) : null}
                  </div>
                  <p id="loyalty-points-help" className="text-xs text-muted-foreground">
                    {t("loyalty.pointRange", { minimum, maximum })}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {loyalty?.availablePoints !== null && (loyalty?.availablePoints ?? 0) < minimum
                    ? t("loyalty.pointsToReward", {
                        points: minimum - (loyalty?.availablePoints ?? 0),
                      })
                    : t("loyalty.rewardUnavailable")}
                </p>
              )}
            </div>
          ) : null}
          {loyalty && loyalty.expectedPoints > 0 ? (
            <p className="mt-3 border-t border-primary/15 pt-3 text-xs font-medium text-primary">
              {t("loyalty.expectedPoints", { points: loyalty.expectedPoints })}
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}
